import React from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Bar, ResponsiveContainer, ComposedChart, ReferenceLine, LabelList  } from "recharts";
import { generateSymmetricTicksAndDomain, generatePositiveIntegerTicksAndDomain } from "../ChartUtils";

const CategoryForumsBarChart = ({ linebarchartData, processedforumsByMonthBarChartLegends }) => {

  const CustomizedTick = ({ x, y, payload }) => {
      return (
          <g transform={`translate(${x},${y})`}>
              <text
                  x={0}
                  y={0}
                  dx={12}
                  dy={12} // Adjust vertical alignment
                  textAnchor="end" // Align the text properly
                  // transform="rotate(270)" // Rotate the text by 90 degrees
                  fill="#616161"
                  style={{ fontSize: "14px" }}
              >
                  {payload.value}
              </text>
          </g>
      );
  };

  // Define fixed keys that should not be treated as categories
  const fixedKeys = new Set(['month', 'projects', 'total']);

  // Extract ALL unique category names from the entire dataset
  const getCategories = () => {
    if (!linebarchartData || linebarchartData.length === 0) return [];
    const categorySet = new Set();
    linebarchartData.forEach(item => {
      Object.keys(item).forEach(key => {
        if (!fixedKeys.has(key)) {
          categorySet.add(key);
        }
      });
    });
    return Array.from(categorySet);
  };

  const categories = getCategories();

  // Get categories that have values in a specific data item
  const getCategoriesForItem = (item) => {
    return categories.filter(category => item[category] !== undefined && item[category] !== null);
  };

  // Use dynamic strokeColor from legends for each category label.
  const legendStrokeColorByLabel = (processedforumsByMonthBarChartLegends || []).reduce((acc, legend) => {
    if (legend?.label && legend?.strokeColor) {
      acc[legend.label.toLowerCase().trim()] = legend.strokeColor;
    }
    return acc;
  }, {});

  // Get color for a category from legend strokeColor.
  const getColorForCategory = (category, index) => {
    return legendStrokeColorByLabel?.[category?.toLowerCase?.().trim?.()] || '#999999';
  };
const sortedData = linebarchartData.map(item => {
  // Create a new item with all categories, defaulting missing ones to 0
  const completeItem = { month: item.month };
  categories.forEach(category => {
    completeItem[category] = item[category] !== undefined ? item[category] : 0;
  });
  
  // Calculate total from actual (non-zero) values only
  const total = Object.keys(item)
    .filter(key => !fixedKeys.has(key))
    .reduce((sum, key) => sum + (parseFloat(item[key]) || 0), 0);
  
  return {
    ...completeItem,
    total: total
  };
});

  const CustomTooltipWithoutZeroValues = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) {
      return null;
    }

    const nonZeroPayload = payload.filter((entry) => Number(entry?.value) !== 0);

    if (!nonZeroPayload.length) {
      return null;
    }

    return (
      <div style={{ backgroundColor: "#fff", padding: "10px", border: "1px solid #ccc" }}>
        <p><strong>{label}</strong></p>
        {nonZeroPayload.map((entry, index) => (
          <p key={index} style={{ color: entry.color }}>
            {entry.name}: {entry.value}
          </p>
        ))}
      </div>
    );
  };


// Your data input
const primaryValues = linebarchartData?.flatMap(item => [
  categories.reduce((sum, key) => sum + parseFloat(item[key] || 0), 0),
]);

const secondaryValues = linebarchartData?.map(item => parseFloat(item.projects));

// Raw min/max
const minSecondaryValue = Math.min(...secondaryValues);
const maxSecondaryValue = Math.max(...secondaryValues);

const minPrimaryValue = Math.min(...primaryValues);
const maxPrimaryValue = Math.max(...primaryValues);


// Determine if negative domain needed for BOTH based on either minPrimary or minSecondary
const needSymmetricDomain = (minPrimaryValue < 0 || minSecondaryValue < 0);
const absMaxSecondary = Math.max(Math.abs(minSecondaryValue), Math.abs(maxSecondaryValue));
const absMaxPrimary = Math.max(Math.abs(minPrimaryValue), Math.abs(maxPrimaryValue));


let {
  domainMax: primaryDomainMax,
  ticks: primaryTicks,
  domainMin: primaryDomainMin,
  
} = needSymmetricDomain
  ? generateSymmetricTicksAndDomain(absMaxPrimary)
  : generatePositiveIntegerTicksAndDomain(maxPrimaryValue);

let {
  
  domainMin: secondaryDomainMin,
  domainMax: secondaryDomainMax,
  ticks: secondaryTicks,
} = needSymmetricDomain
  ? generateSymmetricTicksAndDomain(absMaxSecondary)
  : generatePositiveIntegerTicksAndDomain(maxSecondaryValue);

  if(absMaxPrimary == 0){
    primaryDomainMax = secondaryDomainMax
    primaryTicks = secondaryTicks
    primaryDomainMin = secondaryDomainMin
    
  }
  
  if(absMaxSecondary == 0){
    secondaryTicks = primaryTicks
    
    secondaryDomainMax = primaryDomainMax
    secondaryDomainMin = primaryDomainMin
  }

  // Calculate dynamic width
  const widthPerCategory = 100; // Base width for each category
  const categoryCount = sortedData.length;
  
  let calculatedWidth = categoryCount * widthPerCategory;

  if(calculatedWidth<=675) {
    calculatedWidth = 645;
  }

  const commonYAxisProps = {
    axisLine: false,
    tickLine: false,
    allowDecimals: false,
  };

  return (
    <div style={{ width: '100%', overflowX: 'hidden', overflowY: 'hidden' }}>
    <ResponsiveContainer width="100%" height={400}>
    {/* <ResponsiveContainer width="100%" height={350}> */}
      <ComposedChart
        // width={calculatedWidth}
        data={sortedData}
        margin={{
          left: 10,
          bottom: 50,
          top: 20,
          right: 10,
          
        }}
      >
        <CartesianGrid horizontal={true} vertical={false} stroke="#DFDFDF" />

        <XAxis 
        interval={0}
          dataKey="month" 
          tick={<CustomizedTick />} 
          tickLine={false} 
          
          
          
        />

        {/* Primary Y Axis */}
        <YAxis
          {...commonYAxisProps}
          yAxisId="left"
          // domain={[0, maxPrimaryValue]}
          domain={[primaryDomainMin, primaryDomainMax]}
          ticks={primaryTicks}
          // ticks={generateTicks(maxPrimaryValue)}
          // label={{
          //   value: "Annualized Estimated Spend (MN USD)",
          //   angle: -90,
          //   position: "insideLeft",
          //   textAnchor: "middle",
          //   dy: 140,
          //   style: { fontSize: "14px", fontWeight: "500", fill: "#616161" },
          // }}
        />

          <YAxis
            {...commonYAxisProps}
            yAxisId="right"
            orientation="right"
            // domain={[0, maxSecondaryValue]}
            domain={[secondaryDomainMin, secondaryDomainMax]}
            // ticks={generateTicks(maxSecondaryValue)}
            ticks={secondaryTicks}
            // ticks={minSecondaryValue >= 0 ? generateTicks(maxSecondaryValue) : generateTicksNegative(maxSecondaryValue, minSecondaryValue)}
            interval={0}
            tick={({ x, y, payload }) => (
              <text x={x} y={y + 4} textAnchor="start" fill="#666">
                {payload.value}
              </text>
            )}
            label={{
              value: "Projects",
              angle: -90,
              position: "insideRight",
              textAnchor: "middle",
              dy: -10,
              dx: 8,
              style: { fontSize: "14px", fontWeight: "500", fill: "#616161"},
            }}
          />

        <ReferenceLine y={0} stroke="#888888" yAxisId="right" />

                <Tooltip content={<CustomTooltipWithoutZeroValues />} />

        {/* Dynamically render Bar components for each category */}
        {categories.map((category, index) => (
          <Bar
            key={category}
            barSize={30}
            name={category}
            stackId="a"
            yAxisId="left"
            dataKey={category}
            fill={getColorForCategory(category, index)}
            {...(index === categories.length - 1 && {
              children: <LabelList dataKey="total" position="top" className='ariba-coverage-bar-label' />
            })}
          />
        ))}
                
              </ComposedChart>
            </ResponsiveContainer>
         </div>
          );
        };

export default CategoryForumsBarChart;
